import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../services/api';
import {
  DevicePhoneMobileIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  ExclamationTriangleIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ChevronLeftIcon,
  WrenchScrewdriverIcon
} from '@heroicons/react/24/outline';

const AdminFcmDevices = () => {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState('all');
  const [minDevices, setMinDevices] = useState(1);
  const [expandedUsers, setExpandedUsers] = useState(new Set());
  const [selectedDevices, setSelectedDevices] = useState(new Set());
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [isCleaningUp, setIsCleaningUp] = useState(false);
  const [cleanupResult, setCleanupResult] = useState(null);
  const [showCleanupModal, setShowCleanupModal] = useState(false);
  const [cleanupDays, setCleanupDays] = useState(30);
  const [maxDevicesPerUser, setMaxDevicesPerUser] = useState(5);

  // 디바이스 목록 조회
  const { data: devicesData, isLoading } = useQuery(
    ['admin-fcm-devices', selectedUser, minDevices, page, searchTerm],
    () => api.get('/admin/fcm-devices', {
      params: {
        user_id: selectedUser === 'all' ? undefined : selectedUser,
        min_devices: minDevices,
        page: page,
        limit: limit,
        search: searchTerm || undefined
      }
    }).then(res => res.data),
    {
      keepPreviousData: true
    }
  );

  // 사용자 목록 조회
  const { data: users } = useQuery(
    'admin-fcm-users',
    () => api.get('/admin/fcm-users').then(res => res.data)
  );

  // 디바이스 삭제
  const deleteMutation = useMutation(
    (deviceIds) => {
      if (Array.isArray(deviceIds)) {
        return Promise.all(deviceIds.map(id => api.delete(`/admin/fcm-devices/${id}`)));
      }
      return api.delete(`/admin/fcm-devices/${deviceIds}`);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('admin-fcm-devices');
        setSelectedDevices(new Set());
        alert('디바이스가 삭제되었습니다.');
      },
      onError: (error) => {
        alert('디바이스 삭제에 실패했습니다.');
      }
    }
  );

  // FCM 디바이스 정리
  const cleanupMutation = useMutation(
    (params) => api.post('/admin/fcm-devices/cleanup', params),
    {
      onMutate: () => {
        setIsCleaningUp(true);
        setCleanupResult(null);
      },
      onSuccess: (response) => {
        setCleanupResult(response.data);
        queryClient.invalidateQueries('admin-fcm-devices');
        setIsCleaningUp(false);
      },
      onError: (error) => {
        alert('디바이스 정리에 실패했습니다.');
        setIsCleaningUp(false);
      }
    }
  );

  const handleCleanupConfirm = () => {
    cleanupMutation.mutate({
      days: cleanupDays,
      max_per_user: maxDevicesPerUser
    });
    setShowCleanupModal(false);
  };

  const handleDelete = (deviceId) => {
    if (window.confirm('이 디바이스를 삭제하시겠습니까?')) {
      deleteMutation.mutate(deviceId);
    }
  };

  const handleDeleteSelected = () => {
    if (selectedDevices.size === 0) {
      alert('삭제할 디바이스를 선택해주세요.');
      return;
    }
    if (window.confirm(`선택한 ${selectedDevices.size}개의 디바이스를 삭제하시겠습니까?`)) {
      deleteMutation.mutate(Array.from(selectedDevices));
    }
  };

  const toggleUserExpanded = (userId) => {
    const newExpanded = new Set(expandedUsers);
    if (newExpanded.has(userId)) {
      newExpanded.delete(userId);
    } else {
      newExpanded.add(userId);
    }
    setExpandedUsers(newExpanded);
  };

  const toggleDeviceSelection = (deviceId) => {
    const newSelected = new Set(selectedDevices);
    if (newSelected.has(deviceId)) {
      newSelected.delete(deviceId);
    } else {
      newSelected.add(deviceId);
    }
    setSelectedDevices(newSelected);
  };

  const toggleAllDevicesForUser = (devices) => {
    const newSelected = new Set(selectedDevices);
    const allSelected = devices.every(d => newSelected.has(d.id));
    
    if (allSelected) {
      devices.forEach(d => newSelected.delete(d.id));
    } else {
      devices.forEach(d => newSelected.add(d.id));
    }
    setSelectedDevices(newSelected);
  };

  // 백엔드에서 페이징된 데이터 추출
  const devices = devicesData?.devices || [];
  const totalDevices = devicesData?.total || 0;
  const totalPages = devicesData?.pages || 1;

  // 사용자별 그룹화
  const groupedDevices = devices.reduce((acc, device) => {
    const key = device.user_id;
    if (!acc[key]) {
      acc[key] = {
        user: {
          id: device.user_id,
          name: device.user_name,
          login_id: device.login_id,
          email: device.email,
          user_type: device.user_type
        },
        devices: []
      };
    }
    acc[key].devices.push(device);
    return acc;
  }, {});

  // 관리자만 기본으로 펼침
  useEffect(() => {
    if (expandedUsers.size === 0) {
      const adminUserIds = Object.values(groupedDevices)
        .filter(({ user }) => user.user_type === 'admin')
        .map(({ user }) => user.id);
      
      if (adminUserIds.length > 0) {
        setExpandedUsers(new Set(adminUserIds));
      }
    }
  }, [groupedDevices]);

  const getUserTypeLabel = (type) => {
    const labels = {
      admin: '관리자',
      contractor: '소장',
      customer: '고객',
      vip_customer: 'VIP'
    };
    return labels[type] || type;
  };

  const getUserTypeColor = (type) => {
    const colors = {
      admin: 'text-red-600',
      contractor: 'text-blue-600',
      customer: 'text-gray-600',
      vip_customer: 'text-purple-600'
    };
    return colors[type] || 'text-gray-600';
  };

  if (isLoading) {
    return (
      <div className="p-8 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
        <p className="mt-2 text-gray-600">로딩 중...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-2xl font-semibold text-gray-900">FCM 디바이스 관리</h1>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowCleanupModal(true)}
              disabled={isCleaningUp}
              className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-4 py-2 rounded-md text-sm font-medium flex items-center gap-2"
            >
              <WrenchScrewdriverIcon className="h-4 w-4" />
              {isCleaningUp ? '정리 중...' : '정리'}
            </button>
            {selectedDevices.size > 0 && (
              <button
                onClick={handleDeleteSelected}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md text-sm font-medium flex items-center gap-2"
              >
                <TrashIcon className="h-4 w-4" />
                선택 삭제 ({selectedDevices.size}개)
              </button>
            )}
          </div>
        </div>
        
        {/* 필터 및 검색 */}
        <div className="bg-white shadow rounded-lg p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                사용자 필터
              </label>
              <select
                value={selectedUser}
                onChange={(e) => {
                  setSelectedUser(e.target.value);
                  setPage(1); // 필터 변경 시 첫 페이지로 리셋
                }}
                className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              >
                <option value="all">전체 사용자</option>
                {users?.map(user => (
                  <option key={user.id} value={user.id}>
                    {user.name} {user.login_id ? `(${user.login_id})` : user.email ? `(이메일: ${user.email})` : '(정보없음)'} - {getUserTypeLabel(user.user_type)}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                최소 디바이스 수
              </label>
              <select
                value={minDevices}
                onChange={(e) => {
                  setMinDevices(parseInt(e.target.value));
                  setPage(1); // 필터 변경 시 첫 페이지로 리셋
                }}
                className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              >
                <option value={1}>1개 이상</option>
                <option value={2}>2개 이상</option>
                <option value={3}>3개 이상</option>
                <option value={4}>4개 이상</option>
                <option value={5}>5개 이상</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                검색
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setPage(1); // 검색 시 첫 페이지로 리셋
                  }}
                  placeholder="이름, ID, 이메일, 디바이스명 검색..."
                  className="w-full border-gray-300 rounded-md shadow-sm pl-10 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              </div>
            </div>
          </div>
        </div>

        {/* 정리 모달 */}
        {showCleanupModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100">
                  <WrenchScrewdriverIcon className="h-6 w-6 text-blue-600" />
                </div>
                <div className="mt-3 text-center">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    디바이스 정리 설정
                  </h3>
                  <div className="mt-4 space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        오래된 비활성 디바이스 삭제 기준 (일)
                      </label>
                      <select
                        value={cleanupDays}
                        onChange={(e) => setCleanupDays(parseInt(e.target.value))}
                        className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      >
                        <option value={10}>10일 이상</option>
                        <option value={20}>20일 이상</option>
                        <option value={30}>30일 이상</option>
                        <option value={60}>60일 이상</option>
                        <option value={90}>90일 이상</option>
                        <option value={100}>100일 이상</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        사용자별 최대 비활성 디바이스 수
                      </label>
                      <select
                        value={maxDevicesPerUser}
                        onChange={(e) => setMaxDevicesPerUser(parseInt(e.target.value))}
                        className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      >
                        <option value={5}>5개</option>
                        <option value={10}>10개</option>
                        <option value={15}>15개</option>
                        <option value={20}>20개</option>
                        <option value={25}>25개</option>
                        <option value={30}>30개</option>
                      </select>
                    </div>
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                      <div className="text-sm text-yellow-800">
                        <strong>정리 내용:</strong>
                        <ul className="mt-1 list-disc list-inside">
                          <li>{cleanupDays}일 이상 사용하지 않은 비활성 디바이스 삭제</li>
                          <li>사용자별 비활성 디바이스를 {maxDevicesPerUser}개로 제한</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                  <div className="mt-6 flex justify-center gap-3">
                    <button
                      onClick={() => setShowCleanupModal(false)}
                      className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 text-sm font-medium"
                    >
                      취소
                    </button>
                    <button
                      onClick={handleCleanupConfirm}
                      disabled={isCleaningUp}
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-400 text-sm font-medium"
                    >
                      {isCleaningUp ? '정리 중...' : '정리 시작'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 정리 결과 */}
        {cleanupResult && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-green-800">디바이스 정리 완료</h3>
                <div className="mt-2 text-sm text-green-700">
                  <ul className="list-disc list-inside space-y-1">
                    <li>오래된 비활성 디바이스 {cleanupResult.oldInactiveDeleted}개 삭제</li>
                    <li>사용자별 초과 비활성 디바이스 {cleanupResult.excessInactiveDeleted}개 삭제</li>
                    <li>총 {cleanupResult.totalDeleted}개 디바이스 정리됨</li>
                  </ul>
                </div>
              </div>
              <div className="ml-auto">
                <button
                  onClick={() => setCleanupResult(null)}
                  className="text-green-400 hover:text-green-600"
                >
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 통계 정보 */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white shadow rounded-lg p-4">
            <div className="text-2xl font-bold text-gray-900">{totalDevices}</div>
            <div className="text-sm text-gray-500">전체 디바이스</div>
          </div>
          <div className="bg-white shadow rounded-lg p-4">
            <div className="text-2xl font-bold text-gray-900">{Object.keys(groupedDevices).length}</div>
            <div className="text-sm text-gray-500">현재 페이지 사용자 수</div>
          </div>
          <div className="bg-white shadow rounded-lg p-4">
            <div className="text-2xl font-bold text-blue-600">{devices.length}</div>
            <div className="text-sm text-gray-500">현재 페이지 디바이스</div>
          </div>
          <div className="bg-white shadow rounded-lg p-4">
            <div className="text-2xl font-bold text-green-600">{page} / {totalPages}</div>
            <div className="text-sm text-gray-500">페이지</div>
          </div>
        </div>

        {/* 디바이스 목록 */}
        <div className="space-y-2">
          {Object.values(groupedDevices).map(({ user, devices }) => {
            const isExpanded = expandedUsers.has(user.id);
            const allSelected = devices.every(d => selectedDevices.has(d.id));
            const someSelected = devices.some(d => selectedDevices.has(d.id));
            
            return (
              <div key={user.id} className="bg-white shadow rounded-lg overflow-hidden">
                <div 
                  className="px-4 py-3 bg-gray-50 border-b border-gray-200 cursor-pointer hover:bg-gray-100"
                  onClick={() => toggleUserExpanded(user.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="mr-2">
                        {isExpanded ? (
                          <ChevronDownIcon className="h-5 w-5 text-gray-500" />
                        ) : (
                          <ChevronRightIcon className="h-5 w-5 text-gray-500" />
                        )}
                      </div>
                      <input
                        type="checkbox"
                        checked={allSelected}
                        indeterminate={someSelected && !allSelected}
                        onChange={(e) => {
                          e.stopPropagation();
                          toggleAllDevicesForUser(devices);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        className="mr-3 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <div>
                        <span className="font-medium text-gray-900">{user.name}</span>
                        <span className="ml-2 text-sm text-gray-500">
                          {user.login_id ? (
                            <>({user.login_id})</>
                          ) : user.email ? (
                            <>(이메일: {user.email})</>
                          ) : (
                            <>(정보없음)</>
                          )}
                        </span>
                        <span className={`ml-2 text-sm font-medium ${getUserTypeColor(user.user_type)}`}>
                          {getUserTypeLabel(user.user_type)}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {devices.length >= 5 && (
                        <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />
                      )}
                      <span className={`text-sm font-medium ${devices.length >= 3 ? 'text-orange-600' : 'text-gray-600'}`}>
                        디바이스 {devices.length}개
                      </span>
                    </div>
                  </div>
                </div>
                
                {isExpanded && (
                  <div className="divide-y divide-gray-200">
                    {devices.map((device) => (
                      <div key={device.id} className="px-4 py-3 hover:bg-gray-50">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <input
                              type="checkbox"
                              checked={selectedDevices.has(device.id)}
                              onChange={() => toggleDeviceSelection(device.id)}
                              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                            />
                            <DevicePhoneMobileIcon className="h-5 w-5 text-gray-400" />
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {device.device_name || '이름 없음'}
                              </div>
                              <div className="text-sm text-gray-500">
                                {device.device_model || '모델 정보 없음'}
                                {device.app_version && ` (v${device.app_version})`}
                              </div>
                              <div className="text-xs text-gray-400">
                                마지막 사용: {new Date(device.last_used).toLocaleString()}
                                {!device.is_active && (
                                  <span className="ml-2 text-red-600">(비활성)</span>
                                )}
                              </div>
                            </div>
                          </div>
                          <button
                            onClick={() => handleDelete(device.id)}
                            className="text-red-600 hover:text-red-800"
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {Object.keys(groupedDevices).length === 0 && (
          <div className="bg-white shadow rounded-lg p-8 text-center text-gray-500">
            <DevicePhoneMobileIcon className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            조건에 맞는 디바이스가 없습니다.
          </div>
        )}

        {/* 페이징 */}
        {totalPages > 1 && (
          <div className="bg-white rounded-lg shadow mt-6 px-4 py-3 flex items-center justify-between sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setPage(prev => Math.max(1, prev - 1))}
                disabled={page === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                이전
              </button>
              <button
                onClick={() => setPage(prev => Math.min(totalPages, prev + 1))}
                disabled={page === totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                다음
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  <span className="font-medium">{((page - 1) * limit) + 1}</span>-<span className="font-medium">{Math.min(page * limit, totalDevices)}</span> / 
                  총 <span className="font-medium">{totalDevices}</span>개
                </p>
              </div>
              <div className="flex items-center gap-2">
                {/* 첫 페이지 */}
                <button
                  onClick={() => setPage(1)}
                  disabled={page === 1}
                  className="relative inline-flex items-center px-3 py-2 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  처음
                </button>
                
                {/* 이전 페이지 */}
                <button
                  onClick={() => setPage(prev => Math.max(1, prev - 10))}
                  disabled={page <= 10}
                  className="relative inline-flex items-center px-2 py-2 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="10페이지 이전"
                >
                  <ChevronLeftIcon className="h-4 w-4" />
                  <ChevronLeftIcon className="h-4 w-4 -ml-1" />
                </button>
                
                <button
                  onClick={() => setPage(prev => Math.max(1, prev - 1))}
                  disabled={page === 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeftIcon className="h-4 w-4" />
                </button>

                {/* 페이지 번호들 */}
                <div className="flex items-center">
                  {(() => {
                    const current = page;
                    const range = 2; // 현재 페이지 좌우로 보여줄 페이지 수
                    const pages = [];
                    
                    let start = Math.max(1, current - range);
                    let end = Math.min(totalPages, current + range);
                    
                    // 시작 부분 조정
                    if (end - start < range * 2) {
                      if (start === 1) {
                        end = Math.min(totalPages, start + range * 2);
                      } else {
                        start = Math.max(1, end - range * 2);
                      }
                    }
                    
                    // 첫 페이지와 간격이 있으면 1과 ... 표시
                    if (start > 1) {
                      pages.push(
                        <button
                          key={1}
                          onClick={() => setPage(1)}
                          className="relative inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 border border-gray-300 rounded-md"
                        >
                          1
                        </button>
                      );
                      if (start > 2) {
                        pages.push(
                          <span key="start-ellipsis" className="px-2 py-2 text-sm text-gray-500">...</span>
                        );
                      }
                    }
                    
                    // 메인 페이지 번호들
                    for (let i = start; i <= end; i++) {
                      pages.push(
                        <button
                          key={i}
                          onClick={() => setPage(i)}
                          className={`relative inline-flex items-center px-3 py-2 text-sm font-medium border rounded-md ${
                            i === current
                              ? 'bg-blue-600 text-white border-blue-600 cursor-default'
                              : 'text-gray-700 hover:bg-gray-50 border-gray-300'
                          }`}
                        >
                          {i}
                        </button>
                      );
                    }
                    
                    // 마지막 페이지와 간격이 있으면 ... 과 마지막 페이지 표시
                    if (end < totalPages) {
                      if (end < totalPages - 1) {
                        pages.push(
                          <span key="end-ellipsis" className="px-2 py-2 text-sm text-gray-500">...</span>
                        );
                      }
                      pages.push(
                        <button
                          key={totalPages}
                          onClick={() => setPage(totalPages)}
                          className="relative inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 border border-gray-300 rounded-md"
                        >
                          {totalPages}
                        </button>
                      );
                    }
                    
                    return <div className="flex items-center gap-1">{pages}</div>;
                  })()}
                </div>

                {/* 다음 페이지 */}
                <button
                  onClick={() => setPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={page === totalPages}
                  className="relative inline-flex items-center px-2 py-2 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRightIcon className="h-4 w-4" />
                </button>
                
                <button
                  onClick={() => setPage(prev => Math.min(totalPages, prev + 10))}
                  disabled={page > totalPages - 10}
                  className="relative inline-flex items-center px-2 py-2 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="10페이지 다음"
                >
                  <ChevronRightIcon className="h-4 w-4" />
                  <ChevronRightIcon className="h-4 w-4 -ml-1" />
                </button>

                {/* 마지막 페이지 */}
                <button
                  onClick={() => setPage(totalPages)}
                  disabled={page === totalPages}
                  className="relative inline-flex items-center px-3 py-2 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  마지막
                </button>
                
                {/* 페이지 직접 입력 */}
                <div className="flex items-center gap-1 ml-2">
                  <input
                    type="number"
                    min="1"
                    max={totalPages}
                    value={page}
                    onChange={(e) => {
                      const newPage = parseInt(e.target.value);
                      if (newPage >= 1 && newPage <= totalPages) {
                        setPage(newPage);
                      }
                    }}
                    className="w-16 px-2 py-1 border border-gray-300 rounded text-sm text-center"
                    placeholder="페이지"
                  />
                  <span className="text-sm text-gray-500">페이지</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminFcmDevices;